import { supabase } from "@/integrations/supabase/client";

const MAX_DRAFTS = 2;

export interface ListingDraft {
  id: string;
  formData: {
    title: string;
    description: string;
    car_brand: string;
    car_model: string;
    car_year_from: string;
    car_year_to: string;
    part_number: string;
    price: string;
    condition: string;
    location: string;
    shipping_size: string;
  };
  allowsPickup: boolean;
  savedAt: number;
  thumbnail?: string;
  /** Uppladdade bild-URL:er som hör till utkastet */
  images?: string[];

}

/** Generate a small dataURL thumbnail from a File (resized, jpeg). */
export async function createDraftThumbnail(file: File, maxSize = 96): Promise<string | undefined> {
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });
    const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.7);
  } catch {
    return undefined;
  }
}

function mapDbRowToDraft(row: any): ListingDraft {
  return {
    id: row.id,
    formData: row.data.formData,
    allowsPickup: row.data.allowsPickup ?? false,
    savedAt: new Date(row.updated_at).getTime(),
    thumbnail: row.thumbnail || undefined,
    images: Array.isArray(row.data.images) ? row.data.images : [],
  };
}


async function getCurrentUserId(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id ?? null;
}

/** Save a draft. If draftId is provided, update that draft; otherwise create new. Returns the draft id. */
export async function saveDraft(
  draft: Omit<ListingDraft, 'savedAt' | 'id'>,
  draftId?: string
): Promise<string> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Du måste vara inloggad för att spara utkast');

  const payload = {
    user_id: userId,
    data: { formData: draft.formData, allowsPickup: draft.allowsPickup, images: draft.images ?? [] },
    thumbnail: draft.thumbnail || null,
  };


  if (draftId) {
    const { error } = await supabase
      .from('listing_drafts')
      .update(payload)
      .eq('id', draftId)
      .eq('user_id', userId);

    if (error) {
      if (error.message.includes('Max 2 utkast')) {
        throw new Error('Du har redan 2 utkast. Ta bort ett för att spara ett nytt.');
      }
      throw error;
    }
    return draftId;
  }

  // Check limit before inserting for a clearer UX
  const { count, error: countError } = await supabase
    .from('listing_drafts')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (countError) throw countError;
  if (count && count >= MAX_DRAFTS) {
    throw new Error('Du har redan 2 utkast. Ta bort ett för att spara ett nytt.');
  }

  const { data, error } = await supabase
    .from('listing_drafts')
    .insert(payload)
    .select('id')
    .single();

  if (error) {
    if (error.message.includes('Max 2 utkast')) {
      throw new Error('Du har redan 2 utkast. Ta bort ett för att spara ett nytt.');
    }
    throw error;
  }

  return data.id;
}

/** Load a specific draft by id */
export async function loadDraft(draftId: string): Promise<ListingDraft | null> {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('listing_drafts')
    .select('*')
    .eq('id', draftId)
    .eq('user_id', userId)
    .single();

  if (error || !data) return null;
  return mapDbRowToDraft(data);
}

/** Get all valid drafts for the current user */
export async function getDrafts(): Promise<ListingDraft[]> {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('listing_drafts')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error || !data) return [];
  return data.map(mapDbRowToDraft);
}

/** Delete a specific draft */
export async function deleteDraft(draftId: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { error } = await supabase
    .from('listing_drafts')
    .delete()
    .eq('id', draftId)
    .eq('user_id', userId);

  if (error) throw error;
}

/** Delete all drafts for the current user */
export async function deleteAllDrafts(): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { error } = await supabase
    .from('listing_drafts')
    .delete()
    .eq('user_id', userId);

  if (error) throw error;
}

/** Check if the current user has any drafts */
export async function hasDraft(): Promise<boolean> {
  const drafts = await getDrafts();
  return drafts.length > 0;
}

/** Get a display label for a draft (title, image fallback, or unnamed fallback) */
export function getDraftLabel(draft: ListingDraft): string {
  if (draft.formData.title?.trim()) return draft.formData.title.trim();
  if (draft.thumbnail) return "Utkast";
  return "Namnlöst utkast";
}
